import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BiChevronDown, BiChevronRight, BiFile, BiFolder, BiGridAlt } from 'react-icons/bi';

// Mirrors PreviewTreeNavigation's ancestor lookup so the current Section/Subsection
// stays expanded and visible in the tree as the active section changes.
const findParentSectionId = (sectionId, sections) => {
  for (const section of sections || []) {
    if (section.subSections?.some((s) => s.id === sectionId)) return section.id;
    const parentId = findParentSectionId(sectionId, section.subSections);
    if (parentId != null) return parentId;
  }
  return null;
};

const isFieldCommented = (field, submission, reviewComments) => {
  const response = submission?.responses?.find((d) => d.formField.id === field.id);
  if (!response) return false;

  return reviewComments?.findIndex((c) => c.responseId === response.id) >= 0;
};

// Mirrors FormSidebar's iterateSectionsAndSetCommentedData: a Section is flagged
// as commented as soon as any of its own fields has a review comment against it.
const computeCommentedSections = (sections, submission, reviewComments, commentedData) => {
  sections?.forEach((section) => {
    section.fields?.forEach((field) => {
      if (!commentedData[section.id]) {
        commentedData[section.id] = isFieldCommented(field, submission, reviewComments);
      }
    });
    if (section.subSections?.length > 0) {
      computeCommentedSections(section.subSections, submission, reviewComments, commentedData);
    }
  });
  return commentedData;
};

const SubmittedResponseTreeNavigation = ({
  name,
  fields,
  sections,
  currentSectionId,
  submittedData,
  reviewComments,
  onSelectRootFields,
  onSelectSection,
  onSelectSubSection,
  onSelectQuestion,
}) => {
  const [expanded, setExpanded] = useState({ 'root-fields': true });
  const [searchQuery, setSearchQuery] = useState('');
  const treeBodyRef = useRef(null);

  const commentedSections = useMemo(
    () => computeCommentedSections(sections, submittedData, reviewComments, {}),
    [sections, submittedData, reviewComments]
  );

  const toggleExpand = (key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Auto-expand ancestor sections/subsections of whichever Section is currently
  // active, so it stays visible in the tree — same behavior as PreviewTreeNavigation.
  useEffect(() => {
    if (currentSectionId == null || currentSectionId === 0) return;

    const ancestorIds = [];
    let curId = currentSectionId;
    while (curId != null) {
      ancestorIds.push(curId);
      curId = findParentSectionId(curId, sections);
    }

    setExpanded((prev) => {
      let changed = false;
      const next = { ...prev };
      ancestorIds.forEach((id) => {
        const key = `section-${id}`;
        if (next[key] === false) {
          next[key] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [currentSectionId, sections]);

  useEffect(() => {
    const container = treeBodyRef.current;
    if (!container || currentSectionId == null) return;

    const target = container.querySelector(`[data-tree-section-id="${currentSectionId}"]`);
    if (!target) return;

    target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [currentSectionId, expanded]);

  // Same live-filter rules PreviewTreeNavigation uses: an empty query matches
  // everything, otherwise a case-insensitive substring match against the name.
  const matchesSearch = (text) => {
    if (!searchQuery) return true;
    return text?.toLowerCase().includes(searchQuery.toLowerCase());
  };

  // A Section/Subsection stays visible while searching if its own title matches,
  // one of its own fields matches, or any of its (sub)sections matches recursively.
  const sectionMatchesSearch = (section) => {
    if (matchesSearch(section.title)) return true;
    if (section.fields?.some((f) => matchesSearch(f.name))) return true;
    return section.subSections?.some((sub) => sectionMatchesSearch(sub));
  };

  const renderQuestionRow = (field, depth, onClick) => (
    <div
      key={field.id}
      role="treeitem"
      data-tree-field-id={field.id}
      className={`builder-tree__item builder-tree__item--preview-field Preview-Question-field builder-tree__item--nested${depth > 0 ? '-2' : ''}`}
      onClick={onClick}
    >
      <BiFile className="builder-tree__icon" aria-hidden="true" />
      <span className="builder-tree__label">{field.name || 'Untitled'}</span>
    </div>
  );

  const renderSection = (section, depth = 0, parentSubSections = null) => {
    if (section.hide_alt || !sectionMatchesSearch(section)) return null;

    const key = `section-${section.id}`;
    const isExpanded = expanded[key] !== false;
    const isContainerActive = currentSectionId === section.id;
    const hasComments = commentedSections?.[section.id] === true;
    const visibleFields = section.fields?.filter((f) => !f.hide_alt && matchesSearch(f.name)) ?? [];
    const visibleSubSections = section.subSections?.filter((s) => !s.hide_alt && sectionMatchesSearch(s)) ?? [];
    const hasChildren = visibleSubSections.length > 0 || visibleFields.length > 0;

    const handleSelect = () => {
      if (depth > 0 && parentSubSections) {
        onSelectSubSection(section.id, parentSubSections);
      } else {
        onSelectSection(section.id);
      }
    };

    const handleFieldClick = (field) => {
      if (!isContainerActive) handleSelect();
      onSelectQuestion?.(field.id);
    };

    return (
      <div key={section.id} role="group">
        <div className="builder-tree__section-row">
          <div
            role="treeitem"
            aria-expanded={hasChildren ? isExpanded : undefined}
            aria-selected={isContainerActive}
            data-tree-section-id={section.id}
            className={`builder-tree__item ${depth > 0 ? 'builder-tree__item--nested' : ''} ${isContainerActive ? 'builder-tree__item--selected' : ''} ${hasComments ? 'builder-tree__item--commented' : ''}`}
            onClick={handleSelect}
          >
            {hasChildren ? (
              <button
                type="button"
                className="builder-ui-btn builder-ui-btn--ghost builder-ui-btn--icon"
                style={{ width: 24, height: 24, padding: 0 }}
                onClick={(e) => { e.stopPropagation(); toggleExpand(key); }}
                aria-label={isExpanded ? 'Collapse' : 'Expand'}
              >
                {isExpanded ? <BiChevronDown /> : <BiChevronRight />}
              </button>
            ) : (
              <span style={{ width: 24, height: 24, display: 'inline-block', flexShrink: 0 }} />
            )}
            <BiFolder className="builder-tree__icon" aria-hidden="true" />
            <span className="builder-tree__label">{section.title}</span>
          </div>
        </div>
        {isExpanded && (
          <>
            {visibleFields.map((field) => renderQuestionRow(field, depth, () => handleFieldClick(field)))}
            {visibleSubSections.map((sub) => renderSection(sub, depth + 1, section.subSections))}
          </>
        )}
      </div>
    );
  };

  const isRootSelected = currentSectionId === 0 || currentSectionId == null;
  const rootKey = 'root-fields';
  const isRootExpanded = expanded[rootKey] !== false;
  const allRootFields = fields?.filter((f) => !f.hide_alt) ?? [];
  const visibleRootFields = allRootFields.filter((f) => matchesSearch(f.name));

  const handleRootFieldClick = (field) => {
    if (!isRootSelected) onSelectRootFields();
    onSelectQuestion?.(field.id);
  };

  return (
    <nav className="builder-tree builder-tree--preview" aria-label="Form structure">
      <div className="builder-tree__header">
        <div className="builder-tree__search">
          <input
            type="search"
            placeholder="Filter tree…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Filter navigation tree"
          />
        </div>
      </div>
      <div className="builder-tree__body" role="tree" ref={treeBodyRef}>
        <div role="group">
          <div
            role="treeitem"
            aria-expanded={allRootFields.length > 0 ? isRootExpanded : undefined}
            aria-selected={isRootSelected}
            data-tree-section-id={0}
            className={`builder-tree__item ${isRootSelected ? 'builder-tree__item--selected' : ''}`}
            onClick={() => onSelectRootFields()}
          >
            {allRootFields.length > 0 ? (
              <button
                type="button"
                className="builder-ui-btn builder-ui-btn--ghost builder-ui-btn--icon"
                style={{ width: 24, height: 24, padding: 0 }}
                onClick={(e) => { e.stopPropagation(); toggleExpand(rootKey); }}
                aria-label={isRootExpanded ? 'Collapse' : 'Expand'}
              >
                {isRootExpanded ? <BiChevronDown /> : <BiChevronRight />}
              </button>
            ) : (
              <span style={{ width: 24, height: 24, display: 'inline-block', flexShrink: 0 }} />
            )}
            <BiGridAlt className="builder-tree__icon" aria-hidden="true" />
            <span className="builder-tree__label">{name}</span>
          </div>
          {isRootExpanded && visibleRootFields.length > 0 && visibleRootFields.map((field) => renderQuestionRow(field, 0, () => handleRootFieldClick(field)))}
        </div>
        {sections?.filter((s) => !s.hide_alt).map((section) => renderSection(section))}
      </div>
    </nav>
  );
};

export default SubmittedResponseTreeNavigation;
